/**
 * Area Calculation Utilities
 * 
 * Functions for calculating floor plan areas using the shoelace formula,
 * with unit conversions between pixels, square meters, and square feet.
 */

import { RoomArea, AreaSummary, Calibration, AreaConfidence } from '../types';

// =========================================
// CONSTANTS
// =========================================

/** Square feet per square meter (precise conversion factor) */
export const SQ_FEET_PER_SQ_METER = 10.7639104167;

/** Meters per foot (for dimension conversions) */
export const METERS_PER_FOOT = 0.3048;

/** Feet per meter */
export const FEET_PER_METER = 3.28084;

// =========================================
// GEOMETRY UTILITIES
// =========================================

export interface Point {
  x: number;
  y: number;
}

/**
 * Calculate the area of a polygon using the Shoelace formula
 * (also known as the Surveyor's formula or Gauss's area formula)
 * 
 * @param points - Array of polygon vertices in order
 * @returns Area in square units (same unit as input coordinates)
 */
export function shoelaceArea(points: Point[]): number {
  if (points.length < 3) return 0;
  
  let area = 0;
  const n = points.length;
  
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  
  return Math.abs(area / 2);
}

/**
 * Check if a polygon is closed (first and last points are the same or very close)
 * 
 * @param points - Array of polygon vertices
 * @param tolerance - Maximum distance to consider as "closed" (in pixels)
 * @returns True if the polygon is closed
 */
export function isPolygonClosed(points: Point[], tolerance: number = 5): boolean {
  if (points.length < 3) return false;
  
  const first = points[0];
  const last = points[points.length - 1];
  
  const distance = Math.sqrt(
    Math.pow(last.x - first.x, 2) + Math.pow(last.y - first.y, 2)
  );
  
  return distance <= tolerance;
}

/**
 * Calculate the perimeter of a polygon
 * 
 * @param points - Array of polygon vertices
 * @returns Perimeter in the same unit as input coordinates
 */
export function polygonPerimeter(points: Point[]): number {
  if (points.length < 2) return 0;
  
  let perimeter = 0;
  const n = points.length;
  
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    perimeter += Math.sqrt(
      Math.pow(points[j].x - points[i].x, 2) + 
      Math.pow(points[j].y - points[i].y, 2)
    );
  }
  
  return perimeter;
}

// =========================================
// UNIT CONVERSIONS
// =========================================

/**
 * Convert square pixels to square meters using calibration scale
 * 
 * @param areaPx2 - Area in square pixels
 * @param calibration - Calibration with pxPerMetre scale
 * @returns Area in square meters
 */
export function px2ToM2(areaPx2: number, calibration: Calibration): number {
  const metresPerPx = 1 / calibration.pxPerMetre;
  return areaPx2 * (metresPerPx * metresPerPx);
}

/**
 * Convert square meters to square feet
 * 
 * @param areaM2 - Area in square meters
 * @returns Area in square feet
 */
export function m2ToFt2(areaM2: number): number {
  return areaM2 * SQ_FEET_PER_SQ_METER;
}

/**
 * Convert square feet to square meters
 * 
 * @param areaFt2 - Area in square feet
 * @returns Area in square meters
 */
export function ft2ToM2(areaFt2: number): number {
  return areaFt2 / SQ_FEET_PER_SQ_METER;
}

/**
 * Convert pixels to meters using calibration
 * 
 * @param pixels - Distance in pixels
 * @param calibration - Calibration with pxPerMetre scale
 * @returns Distance in meters
 */
export function pxToMeters(pixels: number, calibration: Calibration): number {
  return pixels / calibration.pxPerMetre;
}

/**
 * Convert meters to pixels using calibration
 * 
 * @param meters - Distance in meters
 * @param calibration - Calibration with pxPerMetre scale
 * @returns Distance in pixels
 */
export function metersToPx(meters: number, calibration: Calibration): number {
  return meters * calibration.pxPerMetre;
}

// =========================================
// AREA FORMATTING
// =========================================

/**
 * Format area in square meters with proper precision
 * 
 * @param areaM2 - Area in square meters
 * @param decimals - Number of decimal places
 * @returns Formatted string (e.g., "45.5 sq m")
 */
export function formatAreaM2(areaM2: number, decimals: number = 1): string {
  return `${areaM2.toFixed(decimals)} sq m`;
}

/**
 * Format area in square feet with proper precision
 * 
 * @param areaFt2 - Area in square feet
 * @param decimals - Number of decimal places
 * @returns Formatted string (e.g., "490 sq ft")
 */
export function formatAreaFt2(areaFt2: number, decimals: number = 0): string {
  return `${areaFt2.toFixed(decimals)} sq ft`;
}

/**
 * Format area in both units (UK estate agent style)
 * 
 * @param areaM2 - Area in square meters
 * @returns Formatted string (e.g., "490 sq ft / 45 sqm")
 */
export function formatAreaBoth(areaM2: number): string {
  const areaFt2 = m2ToFt2(areaM2);
  return `${Math.round(areaFt2)} sq ft / ${Math.round(areaM2)} sqm`;
}

/**
 * Format dimensions in feet and inches (e.g., "11'5")
 * 
 * @param meters - Length in meters
 * @returns Formatted string in feet and inches
 */
export function formatFeetInches(meters: number): string {
  const totalFeet = meters * FEET_PER_METER;
  const feet = Math.floor(totalFeet);
  const inches = Math.round((totalFeet - feet) * 12);
  
  if (inches === 12) {
    return `${feet + 1}'0"`;
  }
  
  return `${feet}'${inches}"`;
}

/**
 * Format dimensions in meters (e.g., "3.5m")
 * 
 * @param meters - Length in meters
 * @param decimals - Number of decimal places
 * @returns Formatted string
 */
export function formatMeters(meters: number, decimals: number = 1): string {
  return `${meters.toFixed(decimals)}m`;
}

/**
 * Format room dimensions in dual format (UK estate agent style)
 * e.g., "11'5 x 8'4\n(3.5m x 2.5m)"
 * 
 * @param widthM - Width in meters
 * @param heightM - Height/depth in meters
 * @returns Multi-line formatted string
 */
export function formatRoomDimensions(widthM: number, heightM: number): string {
  const imperial = `${formatFeetInches(widthM)} x ${formatFeetInches(heightM)}`;
  const metric = `(${formatMeters(widthM)} x ${formatMeters(heightM)})`;
  return `${imperial}\n${metric}`;
}

// =========================================
// ROOM AREA CALCULATION
// =========================================

/**
 * Calculate area for a single room
 * 
 * @param roomId - Unique room identifier
 * @param label - Room label/name
 * @param polygonPoints - Points defining the room boundary
 * @param calibration - Optional calibration for real-world units
 * @returns RoomArea object with calculated values
 */
export function calculateRoomArea(
  roomId: string,
  label: string,
  polygonPoints: Point[],
  calibration?: Calibration
): RoomArea {
  const areaPx2 = shoelaceArea(polygonPoints);
  const isClosed = isPolygonClosed(polygonPoints);
  
  let areaM2: number | null = null;
  let areaFt2: number | null = null;
  
  if (calibration && calibration.pxPerMetre > 0) {
    areaM2 = px2ToM2(areaPx2, calibration);
    areaFt2 = m2ToFt2(areaM2);
  }
  
  return {
    roomId,
    label,
    polygonPoints,
    areaPx2,
    areaM2,
    areaFt2,
    isClosed
  };
}

/**
 * Calculate confidence level based on room closure percentage and scale setting
 * 
 * @param rooms - Array of room areas
 * @param hasScale - Whether a calibration scale has been set
 * @returns Confidence level
 */
export function calculateConfidence(rooms: RoomArea[], hasScale: boolean): AreaConfidence {
  if (!hasScale) return 'low';
  
  if (rooms.length === 0) return 'low';
  
  const closedCount = rooms.filter(r => r.isClosed).length;
  const closedPercentage = (closedCount / rooms.length) * 100;
  
  if (closedPercentage >= 80) return 'high';
  if (closedPercentage >= 50) return 'medium';
  return 'low';
}

/**
 * Calculate area summary from all rooms
 * 
 * @param rooms - Array of room areas
 * @param calibration - Optional calibration
 * @returns AreaSummary with totals and confidence
 */
export function calculateAreaSummary(
  rooms: RoomArea[],
  calibration?: Calibration
): AreaSummary {
  const hasScale = !!(calibration && calibration.pxPerMetre > 0);
  
  let totalM2: number | null = null;
  let totalFt2: number | null = null;
  
  if (hasScale) {
    const closedRooms = rooms.filter(r => r.isClosed && r.areaM2 !== null);
    totalM2 = closedRooms.reduce((sum, r) => sum + (r.areaM2 || 0), 0);
    totalFt2 = m2ToFt2(totalM2);
  }
  
  const closedCount = rooms.filter(r => r.isClosed).length;
  const closedRoomPercentage = rooms.length > 0 
    ? (closedCount / rooms.length) * 100 
    : 0;
  
  const confidence = calculateConfidence(rooms, hasScale);
  
  return {
    totalM2,
    totalFt2,
    rooms,
    confidence,
    hasScale,
    closedRoomPercentage
  };
}

// =========================================
// CALIBRATION HELPERS
// =========================================

/**
 * Create calibration from a known measurement
 * 
 * @param pixelDistance - Distance in pixels
 * @param realDistanceMeters - Real-world distance in meters
 * @param referenceLineId - Optional ID of the reference line used
 * @returns Calibration object
 */
export function createCalibration(
  pixelDistance: number,
  realDistanceMeters: number,
  referenceLineId?: string
): Calibration {
  const pxPerMetre = pixelDistance / realDistanceMeters;
  
  return {
    pxPerMetre,
    referenceLineId,
    confidence: 'high' // User-set calibration is high confidence
  };
}

/**
 * Create calibration from feet and inches
 * 
 * @param pixelDistance - Distance in pixels
 * @param feet - Feet component
 * @param inches - Inches component
 * @param referenceLineId - Optional reference line ID
 * @returns Calibration object
 */
export function createCalibrationFromFeetInches(
  pixelDistance: number,
  feet: number,
  inches: number,
  referenceLineId?: string
): Calibration {
  const totalFeet = feet + (inches / 12);
  const meters = totalFeet * METERS_PER_FOOT;
  return createCalibration(pixelDistance, meters, referenceLineId);
}
